// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import _isEqual from 'lodash/isEqual';
import _noop from 'lodash/noop';
import toLower from 'lodash/toLower';

import { Button, Space, Conditional } from '@cashfree-intl/coherent';

// Hooks
import { useClickOutside } from './hooks';

import FilterPopoverTrigger from './FilterPopoverTrigger';

// Styled
import {
    StyledCTAWrapper,
    StyledPopup,
    StyledPopupContent,
    StyledCheckbox,
    StyledInputField,
} from './styled';

const FilterPopover = ({
    value = {},
    children,
    onApply,
    onClear = _noop,
    trigger = FilterPopoverTrigger,
    appliedFiltersCount = 0,
    selectedFiltersCount = 0,
    buttonDisplayText = 'Filter',
    ...rest
}) => {
    const wrapperRef = useRef(null);
    const triggerRef = useRef(null);
    const [data, setData] = useState(value);
    const [isOpen, setOpen] = useState(false);
    const [saveFilterGroup, resetFilterGroupSelection] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [errors, setErrors] = useState({});

    useEffect(() => {
        setData(value);
    }, [value]);

    const setClose = () => setOpen(false);

    const callback = () => {
        setData(value);
        setClose();
    };

    const onChange = (_, eData) => {
        let val;
        const { name, type } = eData;

        switch (type) {
            case 'checkbox':
                val = eData.checked;
                break;

            default:
                val = eData.value;
                break;
        }

        const updatedData = { ...data, [name]: val };

        if (!val) {
            delete updatedData[name];
        }

        setData(updatedData);
    };

    const clearAll = () => {
        setData({});
        onApply({});
        setClose();
        onClear();
    };

    const handleApply = () => {
        // check if save filter is intended and it bypass the validation check
        if (saveFilterGroup && errors.groupName && !rest.applyButtonText) return;

        setData(data);
        onApply(data, rest.errorMessage ? '' : groupName);
        setClose();
        resetFilterGroupSelection(false);
        setGroupName('');
    };

    useClickOutside(wrapperRef, callback, triggerRef);

    const localCount = Object.keys(data).length;

    const disableClearButton =
        !selectedFiltersCount && !appliedFiltersCount && !localCount;

    const disableApplyButton =
        _isEqual(value, data) &&
        selectedFiltersCount === 0 &&
        appliedFiltersCount === 0;

    const TriggerElement = trigger;

    let buttonText = saveFilterGroup ? 'Save & Apply' : 'Apply';
    if (rest.applyButtonText) buttonText = rest.applyButtonText;

    return (
        <StyledPopup
            on="click"
            flowing
            open={isOpen}
            position="bottom left"
            trigger={
                <TriggerElement
                    open={isOpen}
                    appliedFiltersCount={appliedFiltersCount || localCount}
                    onClick={() => setOpen(isOpenPopover => !isOpenPopover)}
                    ref={triggerRef}
                    text={buttonDisplayText}
                />
            }
            onOpen={() => setOpen(true)}
            content={
                <StyledPopupContent ref={wrapperRef} style={{ width: 444 }}>
                    <div className="content">{children(data, onChange, handleApply)}</div>
                    <StyledCTAWrapper className="pl-4 pb-4 pt-2">
                        <StyledCheckbox
                            label="Save selected filter group for future"
                            checked={saveFilterGroup}
                            disabled={rest.errorMessage}
                            onChange={(e, { checked }) => {
                                resetFilterGroupSelection(checked);
                            }}
                            data-event-name="Checkbox_Save_Selected_Filter"
                            data-event-properties={JSON.stringify({
                                value: saveFilterGroup,
                            })}
                        />
                        <Space
                            justifyContent='end'
                            alignItems={saveFilterGroup ? 'baseline' : 'center'}
                            className={saveFilterGroup ? 'mt-1' : ''}
                        >
                            <Conditional if={!saveFilterGroup || rest.errorMessage}>
                                <Button
                                    link
                                    size="small"
                                    onClick={clearAll}
                                    disabled={disableClearButton}
                                >
                                    Clear All
                                </Button>
                            </Conditional>
                            <Conditional if={saveFilterGroup && !rest.errorMessage}>
                                <StyledInputField
                                    className="m-0 mb-1"
                                    fluid
                                    id="groupName"
                                    name="groupName"
                                    value={groupName}
                                    placeholder="Group Name"
                                    touched={{ groupName: true }}
                                    errors={errors}
                                    onChange={e => {
                                        setGroupName(e.target.value);
                                        if (e.target.value) {
                                            const errors = {};
                                            if (rest?.savedFilterNames?.some(name => toLower(name) === toLower(e.target.value))) {
                                                errors.groupName = 'A filter with this name already exists';
                                            }
                                            setErrors(errors);
                                        } else {
                                            setErrors({ groupName: 'This is a required field' });
                                        }
                                    }}
                                />
                            </Conditional>
                            <Button
                                primary
                                size={saveFilterGroup ? "medium" : "small"}
                                className={saveFilterGroup ? "ml-1 mr-4" : "ml-2 mr-4"}
                                onClick={handleApply}
                                disabled={disableApplyButton}
                            >
                                {buttonText}
                            </Button>
                        </Space>
                    </StyledCTAWrapper>
                </StyledPopupContent>
            }
            {...rest}
        />
    );
};

export default FilterPopover;
